import once from 'lodash/once';
import { createReducer } from 'redux-act';
import { createSelector } from 'reselect';
import { computeLabel } from './actions';
import { createResource } from './createResource';
export function createConfigurableResource(serviceName, resourceName, options) {
  const resource = createResource(`${serviceName}.${resourceName}`, {
    fetchPath: '',
    fetchOnePath: '',
    postPath: options?.fetchPath || '',
    putPath: options?.fetchOnePath || '',
    patchPath: options?.fetchOnePath || '',
    deletePath: options?.fetchOnePath || '',
    defaultFetchParams: {},
    getFetchResponseMeta: () => ({}),
    getFetchOneResponseData: response => response.data,
    getResourceId: r => r.id,
    getFetchResponseData: response => response.data,
    statePath: `${serviceName}.${resourceName}`,
    ...options
  });
  function fetch(url, fetchOptions) {
    return resource.fetch(url, {
      ...fetchOptions,
      params: {
        ...resource.options.defaultFetchParams,
        ...fetchOptions?.params
      }
    });
  }
  fetch.failure = resource.fetch.failure;
  fetch.start = resource.fetch.start;
  fetch.success = resource.fetch.success;
  const listReducer = {
    list: createReducer({
      [resource.fetch.success.getType()]: (state, {
        label,
        response
      }) => {
        const responseData = resource.options.getFetchResponseData(response);
        const logErrorOnce = once(console.error);
        return {
          ...state,
          data: {
            ...state.data,
            ...responseData.reduce((acc, cur) => {
              const id = resource.options.getResourceId(cur, label);
              if (!id) logErrorOnce(`Missing id in resource ${resource.name}. Please define the resource's \`getResourceId()\` or make sure it doesn't return undefined.`, {
                row: cur
              });
              acc[id] = cur;
              return acc;
            }, {})
          },
          [label]: {
            ...resource.options.getFetchResponseMeta(response),
            ids: responseData.map(item => resource.options.getResourceId(item, label))
          }
        };
      }
    }, {})
  };
  const detailReducer = {
    detail: createReducer({
      [resource.fetchOne.success.getType()]: (state, {
        label,
        response
      }) => {
        const data = resource.options.getFetchOneResponseData(response);
        return {
          ...state,
          [label]: data
        };
      },
      [resource.patchOne.start.getType()]: (state, {
        label,
        optimistic
      }) => {
        if (!optimistic) {
          return state;
        }
        return {
          ...state,
          [label]: optimistic.success
        };
      },
      [resource.patchOne.failure.getType()]: (state, {
        label,
        optimistic
      }) => {
        if (!optimistic) {
          return state;
        }
        return {
          ...state,
          [label]: optimistic.failure
        };
      }
    }, {})
  };
  function selectRawState(state) {
    const statePaths = resource.options.statePath.split('.');
    return statePaths.reduce((acc, cur) => acc[cur], state);
  }
  const selectRawList = createSelector([selectRawState], rawState => rawState.list);
  const selectListData = createSelector([selectRawList], rawList => rawList?.data);
  const selectListMeta = createSelector([selectRawList, (_, label) => label], (rawList, label) => rawList ? rawList[label] : undefined);
  const selectList = createSelector([selectListData, selectListMeta], (data, meta) => meta ? meta.ids.map(id => data ? data[id] : undefined).filter(Boolean) : undefined);
  const selectSummary = createSelector([selectListData, (_, resourceId) => resourceId], (data, resourceId) => data?.[resourceId]);
  const selectDetail = createSelector([selectRawState, (_, label) => label], (rawState, label) => rawState.detail?.[label]);
  return {
    ...resource,
    computeLabel,
    fetch,
    listReducer,
    detailReducer,
    selectRawState,
    selectRawList,
    selectListData,
    selectListMeta,
    selectList,
    selectSummary,
    selectDetail
  };
}